import type { ReactNode } from "react";
import type { ExperienceState } from "@/types/state";
import { sanitizeHtml } from "@/modules/sanitizeHtml";

interface RecipeMetadata {
  prepTime?: number | string;
  cookTime?: number | string;
  totalTime?: number | string;
  servings?: number | string;
  calories?: number | string;
}

// Landscape method window rules also cover .experience-container--landscape (the immersive layout)
const WRAPPER =
  "recipe-summary-wrapper tw:overflow-y-auto tw:[padding:0_16px_0_16px] tw:[.swiper-slide_&]:h-full tw:[.method-shop-window>&]:[border:solid_1px_transparent] tw:[:is(.landscape,.experience-container--landscape)_.method-shop-window_&]:pr-[6px] tw:[:is(.landscape,.experience-container--landscape)_.method-shop-window_&]:pl-[6px]";
const ATTRIBUTES =
  "recipe-attributes tw:flex tw:flex-row tw:flex-wrap tw:justify-center tw:bg-[#fff] tw:[padding:2px_0] tw:[:is(.landscape,.experience-container--landscape)_.method-shop-window_&]:grid tw:[:is(.landscape,.experience-container--landscape)_.method-shop-window_&]:[grid-template-columns:repeat(auto-fit,minmax(max(110px,50%_-_8px),1fr))] tw:[:is(.landscape,.experience-container--landscape)_.method-shop-window_&]:justify-items-center tw:[:is(.landscape,.experience-container--landscape)_.method-shop-window_&]:gap-x-[16px] tw:[:is(.landscape,.experience-container--landscape)_.method-shop-window_&]:gap-y-[4px]";
const ATTRIBUTE =
  "tw:[padding:2px_10px] tw:[&.prep-time]:pl-0 tw:[:is(.landscape,.experience-container--landscape)_.method-shop-window_&]:[min-width:0] tw:[:is(.landscape,.experience-container--landscape)_.method-shop-window_&]:box-border tw:[:is(.landscape,.experience-container--landscape)_.method-shop-window_&]:[padding:2px_6px] tw:[:is(.landscape,.experience-container--landscape)_.method-shop-window_&]:whitespace-normal tw:[:is(.landscape,.experience-container--landscape)_.method-shop-window_&]:[overflow-wrap:break-word] tw:[:is(.landscape,.experience-container--landscape)_.method-shop-window_&]:text-[14px] tw:[:is(.landscape,.experience-container--landscape)_.method-shop-window_&:last-child:nth-child(odd)]:[grid-column:1/-1]";
const BODY =
  "recipe-summary-body tw:flex tw:h-full tw:flex-col tw:justify-center tw:[.landscape_.product-shop-window_.swiper-slide_&]:grow";
const VALUE =
  "value tw:[:is(.landscape,.experience-container--landscape)_.method-shop-window_&]:whitespace-nowrap";

type RecipeSummaryProps = {
  experienceState: ExperienceState;
  hasDescription?: boolean;
};

function getAttribute(name: string, title: string, value: ReactNode) {
  return (
    <div key={name} className={`attribute ${name} ${ATTRIBUTE}`}>
      <span
        className="title strong"
        dangerouslySetInnerHTML={{ __html: title }}
      />
      <span className={VALUE}>: {value}</span>
    </div>
  );
}

function getAttributes(metadata?: RecipeMetadata | null) {
  if (metadata) {
    const attrs = [];
    if (metadata.prepTime) {
      attrs.push(
        getAttribute("prep-time", "Prep&nbspTime", `${metadata.prepTime} min`),
      );
    }
    if (metadata.cookTime) {
      attrs.push(
        getAttribute("cook-time", "Cook&nbspTime", `${metadata.cookTime} min`),
      );
    }
    if (metadata.totalTime) {
      attrs.push(
        getAttribute(
          "total-time",
          "Total&nbspTime",
          `${metadata.totalTime} min`,
        ),
      );
    }
    if (metadata.servings) {
      attrs.push(getAttribute("servings", "Servings", metadata.servings));
    }
    if (metadata.calories) {
      attrs.push(getAttribute("calories", "Calories", metadata.calories));
    }

    if (attrs.length > 0) {
      return (
        <div className="recipe-attribute-wrapper">
          <div className="separator horizontal" />
          <div className={ATTRIBUTES}>{attrs}</div>
          <div className="separator horizontal" />
        </div>
      );
    }
  }

  return null;
}

export default function RecipeSummary({
  experienceState,
  hasDescription,
}: RecipeSummaryProps) {
  const experienceData = experienceState.experienceData;
  const title = experienceData.title || experienceData.name;
  let body: ReactNode;

  if (hasDescription) {
    body = (
      <div className={BODY}>
        <h2 className="recipe-subtitle header-2">
          {experienceData.subtitle as string}
        </h2>
        <div
          className="recipe-description tw:mt-[10px]"
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(experienceData.description ?? ""),
          }}
        />
      </div>
    );
  } else {
    body = <div className={BODY}></div>;
  }

  return (
    <div className={WRAPPER}>
      <div className="recipe-summary tw:flex tw:min-h-full tw:flex-col tw:[.portrait_&]:justify-center">
        <div className="recipe-summary-header tw:mt-[5px] tw:text-center tw:[.landscape_&]:grow tw:[.landscape_&]:[flex-basis:0] tw:[.landscape_.product-shop-window_.swiper-slide_&]:grow-0">
          <h1 className="recipe-title header-1">{title}</h1>
          {getAttributes(experienceData.recipeMetadata as RecipeMetadata)}
        </div>
        {body}
      </div>
    </div>
  );
}
